/**
 * Admin header "Staff Sync Alerts" badge — Feb 2026.
 *
 * Sits next to `ServiceM8ReviewBadge` in the admin portal header
 * (`PortalShell.jsx`) and polls
 * `GET /api/admin/integrations/servicem8/alerts/open-count` every 30
 * seconds. Self-hides when the count is 0.
 *
 * Click -> jumps to Settings -> ServiceM8 Scheduling, where the Staff
 * Sync Alerts panel lists and resolves them.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 30_000;

export function ServiceM8AlertsBadge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/integrations/servicem8/alerts/open-count");
        if (mounted) setCount(data.count || 0);
      } catch { /* silent — badge just stays stale/hidden */ }
    };
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    // Resolving an alert or running "Check now" dispatches this event so
    // the header badge updates immediately instead of waiting up to 30s.
    window.addEventListener("sm8-alerts-changed", load);
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener("sm8-alerts-changed", load);
    };
  }, [isAdmin]);

  if (!isAdmin || count === 0) return null;

  return (
    <button
      type="button"
      data-testid="sm8-alerts-count-badge"
      onClick={() => navigate("/admin/settings/servicem8")}
      title={`${count} ServiceM8 sync alert${count === 1 ? "" : "s"} need attention`}
      className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50
                 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-100 transition-colors"
    >
      <ShieldAlert className="h-3.5 w-3.5" />
      {count} sync alert{count === 1 ? "" : "s"}
    </button>
  );
}
